import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Cube, Timer, CheckCircle, Trash, DownloadSimple, CaretLeft, Clock } from "@phosphor-icons/react";
import { useEscStep } from "@/lib/escBack";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const minsLabel = (m) => {
  m = parseInt(m) || 0;
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mi = m % 60;
  return [d ? `${d}d` : "", h ? `${h}h` : "", mi || (!d && !h) ? `${mi}m` : ""].filter(Boolean).join(" ");
};

const downloadFile = async (url, filename) => {
  try {
    const res = await axios.get(url, { responseType: "blob" });
    const objUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a"); link.href = objUrl; link.setAttribute("download", filename);
    document.body.appendChild(link); link.click(); link.remove();
    window.URL.revokeObjectURL(objUrl);
  } catch (err) { alert("Download failed: " + (err.response?.data?.detail || err.message)); }
};

const CostField = ({ label, k, form, setForm, step = "0.01", w = "w-36" }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type="number" step={step} value={form[k]} data-testid={`mfg-${k}-input`}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={w} />
  </div>
);

// Per-unit rate field: user enters ₹/unit, we show × qty = ₹total live
const PerUnitCostField = ({ label, k, form, setForm, qty, unit }) => {
  const perUnit = parseFloat(form[k]) || 0;
  const total = perUnit * qty;
  return (
    <div>
      <Label className="text-xs">{label} (₹/{unit})</Label>
      <div className="flex items-center gap-2">
        <Input type="number" step="0.0001" value={form[k]} data-testid={`mfg-${k}-input`}
          onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-32" />
        <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap" data-testid={`mfg-${k}-total`}>
          × {qty} = <span className="font-black text-slate-900">₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </span>
      </div>
    </div>
  );
};

// Stopwatch: start/stop; on stop the elapsed minutes are added to the field
const TimerWidget = ({ onStop }) => {
  const [running, setRunning] = useState(false);
  const [sec, setSec] = useState(0);
  const ref = useRef(null);
  useEffect(() => () => clearInterval(ref.current), []);
  const toggle = () => {
    if (running) {
      clearInterval(ref.current); setRunning(false);
      onStop(Math.max(1, Math.ceil(sec / 60))); setSec(0);
    } else {
      setRunning(true);
      ref.current = setInterval(() => setSec((s) => s + 1), 1000);
    }
  };
  const mm = String(Math.floor(sec / 60)).padStart(2, "0"), ss = String(sec % 60).padStart(2, "0");
  return (
    <button type="button" onClick={toggle} data-testid="mfg-timer-btn"
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-xs font-black border transition-colors ${running ? "bg-rose-600 text-white border-rose-600 animate-pulse" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}>
      <Clock size={14} /> {running ? `⏹ ${mm}:${ss} Stop` : "▶ Start Timer"}
    </button>
  );
};

export default function Manufacturing() {
  const [view, setView] = useState("lots");            // lots | items
  const [filter, setFilter] = useState("under_process");
  const [batches, setBatches] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);              // grouped items
  const [openItem, setOpenItem] = useState(null);      // {item_name, unit}

  const fetchAll = async () => {
    try {
      const [rb, ri] = await Promise.all([
        axios.get(`${API}/manufacturing/batches`),
        axios.get(`${API}/manufacturing/items`),
      ]);
      setBatches(rb.data.items || []); setSummary(rb.data.summary || null);
      setItems(ri.data.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { fetchAll(); }, []);
  useEscStep(() => {
    if (open) { setOpen(null); setForm(null); return true; }
    if (openItem) { setOpenItem(null); return true; }
    return false;
  });

  const n = (v) => parseFloat(v) || 0;
  const visible = batches.filter((b) => filter === "all" || b.status === filter);

  const openBatch = (b) => {
    if (open === b.id) { setOpen(null); setForm(null); return; }
    setOpen(b.id);
    const q = b.qty || 1;
    setForm({
      raw_rate: b.raw_rate ?? 0,
      power_per_unit: q > 0 ? +((b.power_cost ?? 0) / q).toFixed(4) : 0,
      machinery_per_unit: q > 0 ? +((b.machinery_cost ?? 0) / q).toFixed(4) : 0,
      labor_per_unit: q > 0 ? +((b.labor_cost ?? 0) / q).toFixed(4) : 0,
      overhead_per_unit: q > 0 ? +((b.overhead_cost ?? 0) / q).toFixed(4) : 0,
      scrap_qty_per_unit: q > 0 ? +((b.scrap_qty ?? 0) / q).toFixed(4) : 0,
      scrap_rate: (b.scrap_qty ?? 0) > 0 ? +((b.scrap_value ?? 0) / b.scrap_qty).toFixed(4) : 0,
      margin_type: b.margin_type || "percent", margin_value: b.margin_value ?? 0,
      days: Math.floor((b.processing_minutes || 0) / 1440),
      hours: Math.floor(((b.processing_minutes || 0) % 1440) / 60),
      minutes: (b.processing_minutes || 0) % 60,
      notes: b.notes || "",
    });
  };

  const live = (b) => {
    if (!form) return b;
    const qty = b.qty || 0;
    const raw = n(form.raw_rate) * qty;
    const power = n(form.power_per_unit) * qty;
    const mach = n(form.machinery_per_unit) * qty;
    const labor = n(form.labor_per_unit) * qty;
    const oh = n(form.overhead_per_unit) * qty;
    const scrapQty = n(form.scrap_qty_per_unit) * qty;
    const scrapValue = n(form.scrap_rate) * scrapQty;
    const total = raw + power + mach + labor + oh - scrapValue;
    const outQty = qty - scrapQty;
    const margin = form.margin_type === "percent" ? total * n(form.margin_value) / 100 : n(form.margin_value) * outQty;
    return { raw_cost: raw, power_cost: power, machinery_cost: mach, labor_cost: labor, overhead_cost: oh,
             scrap_qty: scrapQty, scrap_value: scrapValue,
             total_cost: total, output_qty: outQty,
             per_unit_cost: outQty > 0 ? total / outQty : 0,
             margin_amount: margin, final_price: total + margin,
             final_per_unit: outQty > 0 ? (total + margin) / outQty : 0,
             profit_per_unit: outQty > 0 ? margin / outQty : 0 };
  };

  const save = async (b, complete = false) => {
    setSaving(true);
    try {
      const qty = b.qty || 0;
      const payload = {
        raw_rate: n(form.raw_rate),
        power_cost: +(n(form.power_per_unit) * qty).toFixed(2),
        machinery_cost: +(n(form.machinery_per_unit) * qty).toFixed(2),
        labor_cost: +(n(form.labor_per_unit) * qty).toFixed(2),
        overhead_cost: +(n(form.overhead_per_unit) * qty).toFixed(2),
        scrap_qty: +(n(form.scrap_qty_per_unit) * qty).toFixed(3),
        scrap_value: +(n(form.scrap_rate) * n(form.scrap_qty_per_unit) * qty).toFixed(2),
        margin_type: form.margin_type, margin_value: n(form.margin_value),
        processing_minutes: (parseInt(form.days) || 0) * 1440 + (parseInt(form.hours) || 0) * 60 + (parseInt(form.minutes) || 0),
        notes: form.notes, save_template: true,
      };
      if (complete) payload.status = "completed";
      await axios.put(`${API}/manufacturing/batches/${b.id}`, payload);
      await fetchAll();
      if (complete) { setOpen(null); setForm(null); }
      else alert("Saved ✓ — Rates saved for this item. Next time this item arrives from the godown, all calculations will be done automatically.");
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const quickComplete = async (b) => {
    try { await axios.put(`${API}/manufacturing/batches/${b.id}`, { status: "completed", save_template: false }); fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const reopen = async (b) => {
    try { await axios.put(`${API}/manufacturing/batches/${b.id}`, { status: "under_process", save_template: false }); fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete lot '${b.item_name}' (${b.qty} ${b.unit})?`)) return;
    try { await axios.delete(`${API}/manufacturing/batches/${b.id}`); if (open === b.id) { setOpen(null); setForm(null); } fetchAll(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const itemLots = openItem ? batches.filter((b) => b.item_name.toLowerCase() === openItem.item_name.toLowerCase() && b.unit === openItem.unit) : [];
  const openItemRow = openItem ? items.find((i) => i.item_name.toLowerCase() === openItem.item_name.toLowerCase() && i.unit === openItem.unit) : null;

  return (
    <div className="p-6 space-y-6" data-testid="manufacturing-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight flex items-center gap-2"><Cube size={30} /> Manufacturing</h1>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mt-1">
          Set item rates once — every next lot from the godown is auto-calculated · only Mark Complete is needed
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border-l-4 border-l-amber-500 border border-slate-200 rounded-sm p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Under Process</p>
            <p className="text-xl font-black text-amber-700" data-testid="mfg-summary-underprocess">{summary.under_process}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Under Process Cost</p>
            <p className="text-xl font-black">{fmt(summary.under_process_cost)}</p>
          </div>
          <div className="bg-white border-l-4 border-l-emerald-500 border border-slate-200 rounded-sm p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Completed</p>
            <p className="text-xl font-black text-emerald-700" data-testid="mfg-summary-completed">{summary.completed}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Completed Final Value</p>
            <p className="text-xl font-black">{fmt(summary.completed_final)}</p>
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-sm w-fit">
          <button onClick={() => { setView("lots"); setOpenItem(null); }} data-testid="mfg-view-lots"
            className={`px-3 py-1.5 text-xs font-semibold rounded-sm ${view === "lots" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>Lots</button>
          <button onClick={() => { setView("items"); setOpen(null); setForm(null); }} data-testid="mfg-view-items"
            className={`px-3 py-1.5 text-xs font-semibold rounded-sm ${view === "items" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>Items (History)</button>
        </div>
        {view === "lots" && (
          <div className="flex gap-1 bg-slate-100 p-1 rounded-sm w-fit">
            {[["under_process", "Under Process"], ["completed", "Completed"], ["all", "All"]].map(([v, lbl]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`mfg-filter-${v}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm ${filter === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>{lbl}</button>
            ))}
          </div>
        )}
      </div>

      {/* ================= ITEMS (History) VIEW ================= */}
      {view === "items" && !openItem && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="mfg-items-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Item (click to open)</th>
                <th className="px-3 py-2 text-left">Unit</th>
                <th className="px-3 py-2 text-right">Received from Godown (lots)</th>
                <th className="px-3 py-2 text-right">Completed</th>
                <th className="px-3 py-2 text-right">Total Qty</th>
                <th className="px-3 py-2 text-right">Total Cost</th>
                <th className="px-3 py-2 text-right">Final Value</th>
                <th className="px-3 py-2 text-right">Final/Unit</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No items have arrived in manufacturing yet.</td></tr>
              ) : items.map((it) => (
                <tr key={`${it.item_name}|${it.unit}`} className="border-b border-slate-100 hover:bg-blue-50/50 cursor-pointer"
                  data-testid={`mfg-item-row-${it.item_name.replace(/\s+/g, "-")}`}
                  onClick={() => setOpenItem({ item_name: it.item_name, unit: it.unit })}>
                  <td className="px-3 py-2 font-black text-blue-800 underline decoration-dotted underline-offset-4">{it.item_name}</td>
                  <td className="px-3 py-2 text-slate-500">{it.unit}</td>
                  <td className="px-3 py-2 text-right font-bold">{it.lots}</td>
                  <td className="px-3 py-2 text-right font-bold text-emerald-700">{it.completed}</td>
                  <td className="px-3 py-2 text-right font-mono">{it.total_input_qty.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(it.total_cost)}</td>
                  <td className="px-3 py-2 text-right font-mono font-black">{fmt(it.total_final)}</td>
                  <td className="px-3 py-2 text-right font-mono text-amber-700 font-bold">{fmt(it.avg_final_per_unit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "items" && openItem && (
        <div className="space-y-4" data-testid="mfg-item-history">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button onClick={() => setOpenItem(null)} data-testid="mfg-item-back"
              className="inline-flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-slate-900">
              <CaretLeft size={15} /> All Items
            </button>
            <div className="flex gap-2">
              <button data-testid="mfg-item-excel"
                onClick={() => downloadFile(`${API}/manufacturing/item-export/excel?item=${encodeURIComponent(openItem.item_name)}&unit=${encodeURIComponent(openItem.unit)}`, `Manufacturing_${openItem.item_name.replace(/\s+/g, "_")}.xlsx`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:text-white hover:bg-emerald-700 border border-emerald-400 rounded-sm transition-colors">
                <DownloadSimple size={13} /> Excel
              </button>
              <button data-testid="mfg-item-pdf"
                onClick={() => downloadFile(`${API}/manufacturing/item-export/pdf?item=${encodeURIComponent(openItem.item_name)}&unit=${encodeURIComponent(openItem.unit)}`, `Manufacturing_${openItem.item_name.replace(/\s+/g, "_")}.pdf`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-700 hover:text-white hover:bg-rose-700 border border-rose-400 rounded-sm transition-colors">
                <DownloadSimple size={13} /> PDF
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-slate-900 rounded-sm p-3 md:col-span-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Item</p>
              <p className="text-base font-black text-white truncate">{openItem.item_name}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Received from Godown</p>
              <p className="text-lg font-black" data-testid="mfg-item-lots-count">{openItemRow?.lots || 0} time{(openItemRow?.lots || 0) === 1 ? "" : "s"}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Completed</p>
              <p className="text-lg font-black text-emerald-700" data-testid="mfg-item-completed-count">{openItemRow?.completed || 0} time{(openItemRow?.completed || 0) === 1 ? "" : "s"}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Total Final Value</p>
              <p className="text-lg font-black">{fmt(openItemRow?.total_final)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Final Rate / {openItem.unit}</p>
              <p className="text-lg font-black text-amber-700">{fmt(openItemRow?.avg_final_per_unit)}</p>
            </div>
          </div>

          {openItemRow?.template && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs font-semibold text-amber-900" data-testid="mfg-item-template">
              Saved rates (per {openItem.unit}): Power {fmt(openItemRow.template.power_per_unit)} · Machinery {fmt(openItemRow.template.machinery_per_unit)} · Labor {fmt(openItemRow.template.labor_per_unit)} · Overheads {fmt(openItemRow.template.overhead_per_unit)} · Margin {openItemRow.template.margin_type === "percent" ? `${openItemRow.template.margin_value}%` : `${fmt(openItemRow.template.margin_value)}/${openItem.unit}`} · Time {minsLabel(openItemRow.template.processing_minutes)} — to edit, change rates in any lot and Save
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="mfg-item-lots-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Godown</th>
                  <th className="px-3 py-2 text-left">Issued To</th>
                  <th className="px-3 py-2 text-right">In Qty</th>
                  <th className="px-3 py-2 text-right">Out Qty</th>
                  <th className="px-3 py-2 text-right">Total Cost</th>
                  <th className="px-3 py-2 text-right">Margin</th>
                  <th className="px-3 py-2 text-right">Final Price</th>
                  <th className="px-3 py-2 text-left">Time</th>
                </tr>
              </thead>
              <tbody>
                {itemLots.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">{b.date}</td>
                    <td className="px-3 py-2">{b.status === "completed"
                      ? <span className="text-[10px] font-black px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">COMPLETED</span>
                      : <span className="text-[10px] font-black px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800">UNDER PROCESS</span>}</td>
                    <td className="px-3 py-2 text-slate-500">{b.godown_name}</td>
                    <td className="px-3 py-2">{b.issued_to || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{b.qty}</td>
                    <td className="px-3 py-2 text-right font-mono">{b.output_qty}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(b.total_cost)}</td>
                    <td className="px-3 py-2 text-right font-mono text-purple-700">{fmt(b.margin_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono font-black">{fmt(b.final_price)}</td>
                    <td className="px-3 py-2 text-xs">{b.processing_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================= LOTS VIEW ================= */}
      {view === "lots" && (
        <div className="space-y-3">
          {visible.length === 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="mfg-empty">
              No lots yet. A lot is created automatically when you do a Stock OUT from the Godown (Purpose = Manufacturing Unit).
            </div>
          )}
          {visible.map((b) => {
            const t = open === b.id && form ? live(b) : b;
            return (
              <div key={b.id} className="bg-white border border-slate-200 rounded-sm" data-testid={`mfg-batch-${b.id}`}>
                <div className="px-4 py-3 flex items-center gap-3 flex-wrap cursor-pointer hover:bg-slate-50" onClick={() => openBatch(b)}>
                  <span className="font-black text-slate-900">{open === b.id ? "▾" : "▸"} {b.item_name}</span>
                  <span className="text-xs font-mono font-bold text-slate-600">{b.qty} {b.unit}</span>
                  <span className="text-xs text-slate-500">{b.date} · {b.godown_name}</span>
                  {b.issued_to && <span className="text-xs text-slate-500">→ <b>{b.issued_to}</b></span>}
                  {b.auto_from_template && <span className="text-[10px] font-black px-2 py-0.5 rounded-sm bg-blue-100 text-blue-700" data-testid={`mfg-auto-badge-${b.id}`}>AUTO CALCULATED</span>}
                  {b.status === "completed" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700"><CheckCircle size={11} /> COMPLETED</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800"><Timer size={11} /> UNDER PROCESS</span>
                  )}
                  <span className="ml-auto text-sm font-black" data-testid={`mfg-batch-final-${b.id}`}>{fmt(b.final_price)}</span>
                  <span className="text-xs font-mono text-slate-500">{fmt(b.final_per_unit)}/{b.unit}</span>
                  {b.status === "under_process" && (
                    <button onClick={(ev) => { ev.stopPropagation(); quickComplete(b); }} data-testid={`mfg-quick-complete-${b.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-black text-white bg-emerald-600 hover:bg-emerald-700 rounded-sm transition-colors">
                      ✓ Mark Complete
                    </button>
                  )}
                  <button onClick={(ev) => { ev.stopPropagation(); remove(b); }} data-testid={`mfg-delete-${b.id}`}
                    className="text-rose-600 hover:text-rose-800"><Trash size={15} /></button>
                </div>

                {open === b.id && form && (
                  <div className="border-t border-slate-200 px-4 py-4 space-y-4 bg-slate-50/60" data-testid={`mfg-costing-${b.id}`}>
                    <div className="flex gap-3 flex-wrap items-end">
                      <div>
                        <Label className="text-xs">Raw Material Rate (₹/{b.unit}) — auto (bill total ÷ qty)</Label>
                        <Input type="number" step="0.01" value={form.raw_rate} data-testid="mfg-raw_rate-input"
                          onChange={(e) => setForm({ ...form, raw_rate: e.target.value })} className="w-44" />
                      </div>
                      <div className="pb-2 text-xs font-bold text-slate-600">× {b.qty} {b.unit} = <span className="font-black text-slate-900">{fmt(t.raw_cost)}</span> Raw Material Cost</div>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                      <PerUnitCostField label="Power / Electricity" k="power_per_unit" form={form} setForm={setForm} qty={b.qty} unit={b.unit} />
                      <PerUnitCostField label="Machinery Usage" k="machinery_per_unit" form={form} setForm={setForm} qty={b.qty} unit={b.unit} />
                      <PerUnitCostField label="Manpower / Labor" k="labor_per_unit" form={form} setForm={setForm} qty={b.qty} unit={b.unit} />
                      <PerUnitCostField label="Other Overheads" k="overhead_per_unit" form={form} setForm={setForm} qty={b.qty} unit={b.unit} />
                      <div>
                        <Label className="text-xs">Scrap Qty ({b.unit} per {b.unit})</Label>
                        <div className="flex items-center gap-2">
                          <Input type="number" step="0.001" value={form.scrap_qty_per_unit} data-testid="mfg-scrap_qty_per_unit-input"
                            onChange={(e) => setForm({ ...form, scrap_qty_per_unit: e.target.value })} className="w-28" />
                          <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap" data-testid="mfg-scrap-qty-total">
                            × {b.qty} = <span className="font-black text-rose-700">{(n(form.scrap_qty_per_unit) * b.qty).toLocaleString("en-IN", { maximumFractionDigits: 3 })} {b.unit} scrap</span>
                          </span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Scrap Rate (₹/{b.unit} of scrap)</Label>
                        <div className="flex items-center gap-2">
                          <Input type="number" step="0.01" value={form.scrap_rate} data-testid="mfg-scrap_rate-input"
                            onChange={(e) => setForm({ ...form, scrap_rate: e.target.value })} className="w-28" />
                          <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap" data-testid="mfg-scrap-value-total">
                            × {(n(form.scrap_qty_per_unit) * b.qty).toLocaleString("en-IN", { maximumFractionDigits: 3 })} {b.unit} = <span className="font-black text-rose-700">− ₹{(n(form.scrap_rate) * n(form.scrap_qty_per_unit) * b.qty).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 flex-wrap items-end">
                      <div>
                        <Label className="text-xs">Profit Margin</Label>
                        <div className="flex gap-1">
                          <select value={form.margin_type} data-testid="mfg-margin-type"
                            onChange={(e) => setForm({ ...form, margin_type: e.target.value })}
                            className="h-9 px-2 border border-slate-200 rounded-sm text-sm font-semibold">
                            <option value="percent">%</option>
                            <option value="amount">₹/{b.unit}</option>
                          </select>
                          <Input type="number" step="0.01" value={form.margin_value} data-testid="mfg-margin-value-input"
                            onChange={(e) => setForm({ ...form, margin_value: e.target.value })} className="w-28" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Processing Time (Days / Hours / Minutes)</Label>
                        <div className="flex gap-1 items-center">
                          <Input type="number" min="0" value={form.days} data-testid="mfg-time-days"
                            onChange={(e) => setForm({ ...form, days: e.target.value })} className="w-16" placeholder="d" />
                          <Input type="number" min="0" value={form.hours} data-testid="mfg-time-hours"
                            onChange={(e) => setForm({ ...form, hours: e.target.value })} className="w-16" placeholder="h" />
                          <Input type="number" min="0" value={form.minutes} data-testid="mfg-time-minutes"
                            onChange={(e) => setForm({ ...form, minutes: e.target.value })} className="w-16" placeholder="m" />
                          <TimerWidget onStop={(mins) => setForm((f) => ({ ...f, minutes: (parseInt(f.minutes) || 0) + mins }))} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-[140px]">
                        <Label className="text-xs">Notes</Label>
                        <Input value={form.notes} data-testid="mfg-notes-input"
                          onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                      </div>
                    </div>

                    <div className="bg-slate-900 rounded-sm p-4 flex gap-6 flex-wrap items-center" data-testid={`mfg-cost-summary-${b.id}`}>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Cost of Production</p>
                        <p className="text-2xl font-black text-emerald-400" data-testid="mfg-total-cost">{fmt(t.total_cost)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Profit Margin</p>
                        <p className="text-2xl font-black text-purple-400" data-testid="mfg-margin-amount">{fmt(t.margin_amount)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Final Price (Cost + Margin)</p>
                        <p className="text-2xl font-black text-white" data-testid="mfg-final-price">{fmt(t.final_price)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Output {Number(t.output_qty || 0).toLocaleString()} {b.unit} · Final/{b.unit}</p>
                        <p className="text-2xl font-black text-amber-400" data-testid="mfg-final-per-unit">{fmt(t.final_per_unit)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Profit / {b.unit}</p>
                        <p className="text-2xl font-black text-sky-400" data-testid="mfg-profit-per-unit">{fmt(t.profit_per_unit ?? (t.output_qty > 0 ? (t.margin_amount || 0) / t.output_qty : 0))}</p>
                      </div>
                      <div className="ml-auto flex gap-2">
                        <Button onClick={() => save(b)} disabled={saving} data-testid="mfg-save-btn"
                          className="bg-white text-slate-900 hover:bg-slate-200 font-bold">{saving ? "Saving…" : "Save (set rates)"}</Button>
                        {b.status === "under_process" ? (
                          <Button onClick={() => save(b, true)} disabled={saving} data-testid="mfg-complete-btn"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">✓ Mark Completed</Button>
                        ) : (
                          <Button onClick={() => reopen(b)} variant="outline" data-testid="mfg-reopen-btn"
                            className="text-white border-slate-500 hover:bg-slate-700">Reopen</Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
